import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Check, ExternalLink, Search, X, Globe, Users, Package, HelpCircle, Zap,
} from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { SEOHead } from '@/components/SEOHead';
import { productSchema, lowestPrice } from '@/lib/seo';

const STORE_BASE = 'https://deinserverhost.de/store/cart.php';

const licenseData = [
  {
    id: 'web-admin',
    name: 'Plesk Web Admin',
    edition: 'Web Admin',
    price: '10.00',
    orderUrl: `${STORE_BASE}?gid=19`,
    popular: false,
    tags: ['admin', '10 domains', 'wordpress', 'single server'],
    domains: '10',
    customerAccounts: false,
    customerPackages: false,
    featureKeys: [
      'licenses.feat_10_domains',
      'licenses.feat_wordpress',
      'licenses.feat_website_db',
      'licenses.feat_email',
      'licenses.feat_plesk',
    ],
    descKey: 'licenses.web_admin_desc',
  },
  {
    id: 'web-pro',
    name: 'Plesk Web Pro',
    edition: 'Web Pro',
    price: '20.00',
    orderUrl: `${STORE_BASE}?gid=19`,
    popular: true,
    tags: ['pro', '30 domains', 'wordpress', 'reseller', 'customer accounts'],
    domains: '30',
    customerAccounts: true,
    customerPackages: true,
    featureKeys: [
      'licenses.feat_30_domains',
      'licenses.feat_wordpress',
      'licenses.feat_customer_accounts',
      'licenses.feat_customer_packages',
      'licenses.feat_plesk',
    ],
    descKey: 'licenses.web_pro_desc',
  },
  {
    id: 'web-host',
    name: 'Plesk Web Host',
    edition: 'Web Host',
    price: '38.00',
    orderUrl: `${STORE_BASE}?gid=19`,
    popular: false,
    tags: ['host', 'unlimited', 'wordpress', 'reseller', 'customer accounts', 'unlimited domains'],
    domains: 'Unlimited',
    customerAccounts: true,
    customerPackages: true,
    featureKeys: [
      'licenses.feat_unlimited_domains',
      'licenses.feat_wordpress',
      'licenses.feat_customer_accounts',
      'licenses.feat_customer_packages',
      'licenses.feat_plesk',
    ],
    descKey: 'licenses.web_host_desc',
  },
];

const faqKeys = [
  { qKey: 'licenses.faq_q1', aKey: 'licenses.faq_a1' },
  { qKey: 'licenses.faq_q2', aKey: 'licenses.faq_a2' },
  { qKey: 'licenses.faq_q3', aKey: 'licenses.faq_a3' },
  { qKey: 'licenses.faq_q4', aKey: 'licenses.faq_a4' },
];

function LicenseCard({ lic, index, t }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`relative flex flex-col rounded-2xl border p-6 ${
        lic.popular
          ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10'
          : 'border-border glass'
      }`}
      data-testid={`license-card-${lic.id}`}
    >
      {lic.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-primary text-xs px-3 py-1 font-bold uppercase tracking-wider">
            {t('common.popular')}
          </Badge>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <img src="/images/plesk.svg" alt="Plesk" title="Plesk" className="w-8 h-8 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-black text-base leading-tight">{lic.name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{t(lic.descKey)}</p>
        </div>
      </div>

      {/* Key specs */}
      <div className="flex flex-wrap gap-2 mb-5">
        <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-muted">
          <Globe className="w-3 h-3 text-primary" />
          {lic.domains} Domains
        </span>
        {lic.customerAccounts && (
          <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-muted">
            <Users className="w-3 h-3 text-primary" />
            {t('licenses.spec_customer_accounts')}
          </span>
        )}
        {lic.customerPackages && (
          <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-muted">
            <Package className="w-3 h-3 text-primary" />
            {t('licenses.spec_packages')}
          </span>
        )}
      </div>

      {/* Features */}
      <ul className="space-y-2 mb-6 flex-1">
        {lic.featureKeys.map((fk) => (
          <li key={fk} className="flex items-center gap-2 text-xs">
            <Check className="w-3.5 h-3.5 text-green-500 shrink-0" />
            <span>{t(fk)}</span>
          </li>
        ))}
      </ul>

      {/* Price + CTA */}
      <div className="flex items-baseline gap-1 mb-4">
        <span className="text-3xl font-black text-primary">€{lic.price}</span>
        <span className="text-xs text-muted-foreground">{t('licenses.perMonth')}</span>
      </div>

      <a href={lic.orderUrl} target="_blank" rel="noopener noreferrer">
        <Button
          className={`w-full text-sm font-bold ${
            lic.popular
              ? 'bg-primary hover:bg-primary/90 glow-hover'
              : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
          }`}
          data-testid={`order-${lic.id}-btn`}
        >
          {t('licenses.orderNow')}
          <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </a>
    </motion.div>
  );
}

export default function LicensesPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const filtered = query.trim()
    ? licenseData.filter((l) =>
        l.name.toLowerCase().includes(query.toLowerCase()) ||
        l.tags.some((tt) => tt.toLowerCase().includes(query.toLowerCase())) ||
        l.domains.toLowerCase().includes(query.toLowerCase())
      )
    : licenseData;

  return (
    <Layout>
      <SEOHead
        title="Plesk Licenses – Web Admin, Pro & Host"
        description={`Plesk licenses for your server from €${lowestPrice(licenseData.map((l) => l.price))}/month – Web Admin, Web Pro and Web Host editions. Monthly billing, no contract, instant activation.`}
        canonical="/licenses"
        schema={productSchema({
          name: 'Plesk License',
          description: 'Plesk Web Admin, Web Pro and Web Host licenses for your server, billed monthly.',
          path: '/licenses',
          image: '/images/plesk.svg',
          offers: licenseData.map((lic) => ({ name: lic.name, price: lic.price, period: 'month' })),
        })}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="flex justify-center mb-6">
              <img src="/images/plesk.svg" alt="Plesk" title="Plesk" className="h-12" />
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              {t('licenses.title')} <span className="text-gradient">{t('licenses.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('licenses.subtitle')}
            </p>
          </motion.div>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="relative max-w-lg mx-auto mb-10"
          >
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('licenses.search_placeholder')}
              className="pl-10 pr-10 h-12 rounded-xl bg-card border-border focus:border-primary text-sm"
              data-testid="license-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
                data-testid="license-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </motion.div>

          {/* License Cards */}
          <AnimatePresence mode="wait">
            {filtered.length > 0 ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-20"
              >
                {filtered.map((lic, i) => (
                  <LicenseCard key={lic.id} lic={lic} index={i} t={t} />
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-center py-20 mb-12"
                data-testid="license-no-results"
              >
                <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
                <p className="text-lg font-semibold mb-2">No license found for "{query}"</p>
                <Button variant="outline" onClick={() => setQuery('')} className="border-primary/30 hover:border-primary">
                  {t('common.viewAll')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* FAQ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">FAQ</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {faqKeys.map((faq, i) => {
                const q = t(faq.qKey);
                const a = t(faq.aKey);
                // Show only if translations exist (not raw keys)
                if (q === faq.qKey) return null;
                return (
                  <motion.div
                    key={faq.qKey}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + i * 0.06 }}
                    className="glass rounded-xl border border-border p-5"
                  >
                    <h3 className="font-bold text-sm mb-2">{q}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{a}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="flex flex-col sm:flex-row items-center justify-between gap-6 p-8 glass rounded-2xl"
          >
            <div>
              <h3 className="text-xl font-bold mb-1">{t('licenses.ctaTitle')}</h3>
              <p className="text-sm text-muted-foreground">{t('licenses.ctaDesc')}</p>
            </div>
            <a
              href="https://deinserverhost.de/store/submitticket.php"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover shrink-0">
                <Zap className="w-4 h-4 mr-2" />
                {t('licenses.ctaBtn')}
              </Button>
            </a>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
